import React, { useCallback, useContext, useEffect, useRef, useState } from "react"
import moment from "moment"
import { Helmet } from "react-helmet"
import firebase from "firebase/app"
import "firebase/firestore"

import FirebaseProvider, { FirebaseContext } from "../components/idealline/Firebase"
import { ClientOnly, Layout } from "../components/common"
import {
    approveTrackUpdate,
    asDate,
    buildAdminDashboard,
    lineSource,
    lineUrl,
} from "../utils/adminDashboard"
import "../styles/admin-dashboard.css"

const iconPaths = {
    track: "M4 12h2l2-5h8l2 5h2v5h-2a2 2 0 0 1-4 0H10a2 2 0 0 1-4 0H4v-5Zm5.5 0h5L13 9h-2l-1.5 3ZM8 16a1 1 0 1 0 0 2 1 1 0 0 0 0-2Zm8 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z",
    inbox: "M4 5h16v13H4V5Zm2 2v7h3l1.5 2h3L15 14h3V7H6Z",
    line: "M5 17c3-8 5 1 8-7 1.5-3.5 3-3 6-3M5 17h.01M19 7h.01",
    member: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 8a7 7 0 0 1 14 0H5Z",
    refresh: "M20 6v5h-5M4 18v-5h5m9.5-3A7 7 0 0 0 6.2 7M5.5 15A7 7 0 0 0 17.8 17",
    arrow: "M5 12h14m-5-5 5 5-5 5",
    warning: "M12 3 2.8 20h18.4L12 3Zm0 6v5m0 3h.01",
    check: "m5 12 4 4L19 6",
}

const Icon = ({ name, size = 20 }) => (
    <svg aria-hidden="true" className="admin-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d={iconPaths[name] || iconPaths.track} />
    </svg>
)

const formatNumber = value => new Intl.NumberFormat("en").format(value || 0)
const formatDate = value => {
    const date = asDate(value)
    return date ? moment(date).fromNow() : "No timestamp"
}
const exactDate = value => {
    const date = asDate(value)
    return date ? date.toLocaleString() : "Timestamp unavailable"
}
const linePointCount = line => Array.isArray(line.points) ? line.points.length : 0
const lineTrackLabel = line => line.trackName || line.track || (typeof line.location === "string" ? line.location : null) || (() => {
    const match = String(line.url || "").match(/^\/go-karting\/([^/?#]+)/)
    return match ? decodeURIComponent(match[1]).replace(/-/g, " ") : "Track not specified"
})()
const safeExternalUrl = value => {
    try {
        const url = new URL(value)
        return ["http:", "https:"].includes(url.protocol) ? url.href : null
    } catch (error) {
        return null
    }
}

const loadAdminStats = async (user, { linePeriod = "30d", scope = "dashboard" } = {}) => {
    const token = await user.getIdToken()
    const query = new URLSearchParams({ linePeriod, scope })
    const response = await fetch(`/api/adminStats?${query.toString()}`, {
        headers: { Authorization: ["Bearer", token].join(" ") },
    })
    if (!response.ok) throw new Error(`Could not load admin statistics (${response.status})`)
    return response.json()
}

const loadLineAuthors = async (firestore, lines) => {
    const uids = [...new Set(lines.map(line => line.user).filter(Boolean))]
    const users = []
    for (let offset = 0; offset < uids.length; offset += 10) {
        const snapshot = await firestore.collection("users").where("uid", "in", uids.slice(offset, offset + 10)).get()
        users.push(...snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id, username: doc.id })))
    }
    return users
}

const LoadingState = () => (
    <div className="admin-state admin-state--loading" aria-live="polite">
        <div className="admin-loader"><span /><span /><span /></div>
        <p>Opening race control…</p>
        <small>Checking access and loading live Firestore data</small>
    </div>
)

const AccessState = ({ type }) => {
    const signedOut = type === "signed-out"
    return (
        <div className="admin-state">
            <div className="admin-state__mark"><Icon name={signedOut ? "member" : "warning"} size={28} /></div>
            <p>{signedOut ? "Admin sign-in required" : "Race control access denied"}</p>
            <small>{signedOut ? "Sign in with an administrator account to continue." : "This account does not have the isAdmin claim."}</small>
            {signedOut && <a className="admin-primary-action" href="/profile?redirect=/admin" target="_blank" rel="noreferrer">Sign in</a>}
        </div>
    )
}

const MetricCard = ({ label, value, note, icon, accent }) => (
    <article className={`admin-metric admin-metric--${accent}`}>
        <div className="admin-metric__icon"><Icon name={icon} size={22} /></div>
        <div>
            <p className="admin-eyebrow">{label}</p>
            <strong>{formatNumber(value)}</strong>
            <span>{note}</span>
        </div>
    </article>
)

const SectionHeader = ({ eyebrow, title, meta }) => (
    <header className="admin-section-header">
        <div>
            <p className="admin-eyebrow">{eyebrow}</p>
            <h2>{title}</h2>
        </div>
        {meta && <span className="admin-section-meta">{meta}</span>}
    </header>
)

const EmptyState = ({ children }) => (
    <div className="admin-empty"><span><Icon name="check" /></span>{children}</div>
)

const LazyPanel = ({ eyebrow, title, summary, open, loading, error, onToggle, children }) => (
    <section className={`admin-lazy-panel${open ? " is-open" : ""}`}>
        <button aria-expanded={open} className="admin-lazy-panel__toggle" onClick={onToggle} type="button">
            <span><span className="admin-eyebrow">{eyebrow}</span><strong>{title}</strong><small>{error ? error.message : summary}</small></span>
            <span>{loading ? "Loading…" : open ? "Collapse" : "Load section"}</span>
        </button>
        {open && <div className="admin-lazy-panel__content">{children}</div>}
    </section>
)

const PendingSuggestions = ({ tracks, projectId, onApprove, approvingTrackId, actionsDisabled }) => (
    <section className="admin-panel admin-panel--inbox">
        <SectionHeader eyebrow="Review queue" title="Pending suggestions" meta={`${tracks.length} awaiting review`} />
        {tracks.length === 0 ? (
            <EmptyState>Inbox clear — there are no pending API submissions.</EmptyState>
        ) : (
            <div className="admin-inbox-list">
                {tracks.slice(0, 12).map(track => {
                    const consoleUrl = `https://console.firebase.google.com/project/${projectId}/firestore/data/~2Ftracks~2F${encodeURIComponent(track.id)}`
                    const mapsUrl = track.googlePlaceId
                        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(track.name || track.googlePlaceId)}&query_place_id=${encodeURIComponent(track.googlePlaceId)}`
                        : null
                    return (
                        <article className="admin-inbox-row" key={track.id}>
                            <div className="admin-inbox-row__pulse" />
                            <div className="admin-inbox-row__main">
                                <div className="admin-inbox-row__title">
                                    <strong>{track.name || "Unnamed track"}</strong>
                                    <span title={exactDate(track.createdAt || track.updatedAt)}>{formatDate(track.createdAt || track.updatedAt)}</span>
                                </div>
                                <div className="admin-tags">
                                    {track.country && <span>{track.country}</span>}
                                    {track.type && <span>{track.type}</span>}
                                    {track.subtype && <span>{track.subtype}</span>}
                                    {track.googleRating && <span>★ {track.googleRating} · {formatNumber(track.googleRatingsTotal)} reviews</span>}
                                </div>
                            </div>
                            <div className="admin-inbox-row__actions">
                                {mapsUrl && <a href={mapsUrl} target="_blank" rel="noreferrer">Map</a>}
                                <a className="admin-row-action" href={consoleUrl} target="_blank" rel="noreferrer">Review <Icon name="arrow" size={15} /></a>
                                <button
                                    className="admin-approve-action"
                                    disabled={actionsDisabled}
                                    onClick={() => onApprove(track)}
                                    type="button"
                                >
                                    <Icon name="check" size={15} /> {approvingTrackId === track.id ? "Approving…" : "Approve"}
                                </button>
                            </div>
                        </article>
                    )
                })}
            </div>
        )}
    </section>
)

const PendingClaims = ({ claims, onReview, reviewingClaimId, actionsDisabled }) => (
    <section className="admin-panel admin-panel--claims">
        <SectionHeader eyebrow="Business ownership" title="Pending claims" meta={`${claims.length} awaiting review`} />
        {claims.length === 0 ? <EmptyState>No business claims need review.</EmptyState> : claims.map(claim => {
            const website = safeExternalUrl(claim.businessWebsite)
            return (
                <article className="admin-claim-row" key={claim.id}>
                    <div className="admin-claim-row__heading">
                        <div>
                            <span className="admin-timeline__icon admin-timeline__icon--member"><Icon name="member" size={17} /></span>
                            <span><strong>{claim.trackName}</strong><small>{claim.claimantName} · {claim.role}</small></span>
                        </div>
                        <time title={exactDate(claim.createdAt)}>{formatDate(claim.createdAt)}</time>
                    </div>
                    <div className="admin-claim-row__details">
                        <a href={`mailto:${encodeURIComponent(claim.businessEmail)}`} target="_blank" rel="noreferrer">{claim.businessEmail}</a>
                        {website && <a href={website} target="_blank" rel="noreferrer">{claim.businessWebsite}</a>}
                        <a href={`/go-karting/${encodeURIComponent(claim.trackSlug)}`} target="_blank" rel="noreferrer">View track</a>
                    </div>
                    <p>{claim.verificationNote}</p>
                    <div className="admin-claim-row__actions">
                        <button disabled={actionsDisabled} onClick={() => onReview(claim, "rejected")} type="button">Reject</button>
                        <button className="admin-approve-action" disabled={actionsDisabled} onClick={() => onReview(claim, "approved")} type="button">
                            <Icon name="check" size={15} /> {reviewingClaimId === claim.id ? "Saving…" : "Approve"}
                        </button>
                    </div>
                </article>
            )
        })}
    </section>
)

const PendingSponsorships = ({ requests, onReview, reviewingRequestId, actionsDisabled }) => (
    <section className="admin-panel admin-panel--claims">
        <SectionHeader eyebrow="Commercial partnerships" title="Sponsorship interest" meta={`${requests.length} awaiting review`} />
        {requests.length === 0 ? <EmptyState>No sponsorship requests need review.</EmptyState> : requests.map(request => {
            const website = safeExternalUrl(request.businessWebsite)
            const targetPath = request.targetPath === "/"
                || request.targetPath === "/go-karting-tracks"
                || /^\/cities\/[a-z0-9-]+$/.test(request.targetPath || "")
                ? request.targetPath
                : null
            return (
                <article className="admin-claim-row" key={request.id}>
                    <div className="admin-claim-row__heading">
                        <div>
                            <span className="admin-timeline__icon admin-timeline__icon--member"><Icon name="inbox" size={17} /></span>
                            <span><strong>{request.companyName}</strong><small>{request.packageName} · €{formatNumber(request.monthlyPriceEur)}/month · {request.targetName}</small></span>
                        </div>
                        <time title={exactDate(request.createdAt)}>{formatDate(request.createdAt)}</time>
                    </div>
                    <div className="admin-claim-row__details">
                        <a href={`mailto:${encodeURIComponent(request.businessEmail)}`} target="_blank" rel="noreferrer">{request.businessEmail}</a>
                        <span>{request.contactName} · {request.role}</span>
                        {website && <a href={website} target="_blank" rel="noreferrer">{request.businessWebsite}</a>}
                        {targetPath && <a href={targetPath} target="_blank" rel="noreferrer">View placement</a>}
                    </div>
                    <p>{request.campaignSummary || "No campaign summary supplied."}</p>
                    <div className="admin-claim-row__actions">
                        <button disabled={actionsDisabled} onClick={() => onReview(request, "rejected")} type="button">Reject</button>
                        <button className="admin-approve-action" disabled={actionsDisabled} onClick={() => onReview(request, "approved")} type="button">
                            <Icon name="check" size={15} /> {reviewingRequestId === request.id ? "Saving…" : "Approve for follow-up"}
                        </button>
                    </div>
                </article>
            )
        })}
    </section>
)

const ActivityChart = ({ days }) => {
    const max = Math.max(1, ...days.map(day => day.count))
    return (
        <div className="admin-activity-chart" aria-label="Activity over the last seven days">
            {days.map(day => (
                <div className="admin-activity-chart__day" key={day.key} title={`${day.count} events on ${day.key}`}>
                    <span className="admin-activity-chart__count">{day.count}</span>
                    <div className="admin-activity-chart__track">
                        <i style={{ height: `${Math.max(day.count ? 16 : 4, (day.count / max) * 58)}px` }} />
                    </div>
                    <small>{day.label}</small>
                </div>
            ))}
        </div>
    )
}

const activityIcon = kind => ({ suggestion: "inbox", track: "track", line: "line", member: "member" }[kind] || "track")

const RecentActivity = ({ activity, days }) => (
    <section className="admin-panel admin-panel--activity">
        <SectionHeader eyebrow="Live pulse" title="Recent activity" meta="Latest 20 events" />
        <ActivityChart days={days} />
        <p className="admin-activity-note">Track and member history, plus the latest 100 racing lines.</p>
        <div className="admin-timeline">
            {activity.length === 0 ? <EmptyState>No timestamped activity yet.</EmptyState> : activity.map(item => {
                const content = (
                    <>
                        <span className={`admin-timeline__icon admin-timeline__icon--${item.kind}`}><Icon name={activityIcon(item.kind)} size={17} /></span>
                        <span className="admin-timeline__copy"><strong>{item.title}</strong><small>{item.detail}</small></span>
                        <time title={exactDate(item.date)}>{formatDate(item.date)}</time>
                    </>
                )
                return item.href
                    ? <a className="admin-timeline__row" href={item.href} key={item.id} target="_blank" rel="noreferrer">{content}</a>
                    : <div className="admin-timeline__row" key={item.id}>{content}</div>
            })}
        </div>
    </section>
)

const avatarFallback = user => (user && (user.displayName || user.username || user.id) || "?").slice(0, 1).toUpperCase()

const RecentLines = ({ lines, usersByUid }) => (
    <section className="admin-panel">
        <SectionHeader eyebrow="Community output" title="Newest racing lines" meta={`${lines.length} recent`} />
        <div className="admin-compact-list">
            {lines.map(line => {
                const user = usersByUid.get(line.user)
                return (
                    <a className="admin-compact-row" href={lineUrl(line)} key={line.id} target="_blank" rel="noreferrer">
                        <span className="admin-avatar">
                            {user && user.photoURL ? <img src={user.photoURL} alt="" /> : avatarFallback(user)}
                        </span>
                        <span className="admin-compact-row__copy">
                            <strong>{line.name || "Unnamed racing line"}</strong>
                            <small>{user && user.displayName || line.user || "Unknown racer"} · {lineTrackLabel(line)}</small>
                            <span className="admin-line-facts">
                                {line.vehicle && <span>{line.vehicle}</span>}
                                <span>{formatNumber(linePointCount(line))} points</span>
                                <span>{line.visibility || "visibility unknown"}</span>
                                <span className={`admin-source-badge admin-source-badge--${lineSource(line).key}`}>{lineSource(line).label}</span>
                            </span>
                        </span>
                        <span className="admin-compact-row__time" title={exactDate(line.created || line.createdAt)}>{formatDate(line.created || line.createdAt)}</span>
                        <Icon name="arrow" size={16} />
                    </a>
                )
            })}
            {lines.length === 0 && <EmptyState>No racing lines found.</EmptyState>}
        </div>
    </section>
)

const SOURCE_PERIODS = [
    { key: "all", label: "Total" },
    { key: "30d", label: "Last 30 days" },
    { key: "3m", label: "Last 3 months" },
    { key: "6m", label: "Last 6 months" },
]

const SourceBreakdown = ({ periods, onLoadPeriod }) => {
    const [selectedPeriod, setSelectedPeriod] = useState("30d")
    const [loadingPeriod, setLoadingPeriod] = useState(null)
    const selected = periods.find(period => period.key === selectedPeriod)
    const sources = selected ? selected.sources : []
    const total = selected ? selected.total : 0

    useEffect(() => {
        if (!loadingPeriod && !periods.some(period => period.key === selectedPeriod)) {
            setSelectedPeriod(periods.some(period => period.key === "30d") ? "30d" : periods[0] && periods[0].key)
        }
    }, [loadingPeriod, periods, selectedPeriod])

    const selectPeriod = async periodKey => {
        setSelectedPeriod(periodKey)
        if (periods.some(period => period.key === periodKey)) return
        setLoadingPeriod(periodKey)
        try {
            const loaded = await onLoadPeriod(periodKey)
            if (loaded === false) setSelectedPeriod(periods.some(period => period.key === "30d") ? "30d" : periods[0] && periods[0].key)
        } catch (loadError) {
            setSelectedPeriod(periods[0] ? periods[0].key : "30d")
        } finally {
            setLoadingPeriod(null)
        }
    }

    return (
        <section className="admin-panel admin-distribution">
            <SectionHeader eyebrow="Creation methods" title="Racing-line sources" meta={loadingPeriod ? "Loading…" : `${selected ? selected.label : "Period"} · ${formatNumber(total)}`} />
            <div className="admin-period-filter" role="group" aria-label="Racing-line source period">
                {SOURCE_PERIODS.map(period => (
                    <button
                        aria-pressed={period.key === selectedPeriod}
                        className={period.key === selectedPeriod ? "is-active" : ""}
                        disabled={Boolean(loadingPeriod)}
                        key={period.key}
                        onClick={() => selectPeriod(period.key)}
                        type="button"
                    >
                        {period.label}
                    </button>
                ))}
            </div>
            <div className="admin-distribution__rows">
                {sources.map(source => (
                    <div className="admin-distribution__row" key={source.key}>
                        <div>
                            <span className={`admin-source-badge admin-source-badge--${source.key}`}>{source.label}</span>
                            <strong>{formatNumber(source.count)}</strong>
                            <small>{source.percent}%</small>
                        </div>
                        <div className="admin-distribution__bar" aria-label={`${source.label}: ${source.count} of ${total}`}>
                            <i className={`admin-distribution__bar--${source.key}`} style={{ width: `${source.percent}%` }} />
                        </div>
                    </div>
                ))}
            </div>
            <p className="admin-activity-note">Periods are loaded on demand and cached. Total is the only view that scans every racing-line record.</p>
        </section>
    )
}

const AccountCreationChart = ({ periods, recency = {}, recentAccounts = [] }) => {
    const [selectedPeriod, setSelectedPeriod] = useState("12m")
    const selected = periods.find(period => period.key === selectedPeriod) || periods[0]
    const months = selected ? selected.months : []
    const total = selected ? selected.total : 0
    const max = Math.max(1, ...months.map(month => month.count))

    return (
        <section className="admin-panel admin-account-growth">
            <SectionHeader eyebrow="Community growth" title="Account creation" meta={`${selected ? selected.label : "Total"} · ${formatNumber(total)}`} />
            <div className="admin-recency-metrics">
                <div><strong>{formatNumber(recency.last24Hours)}</strong><span>New in 24 hours</span></div>
                <div><strong>{formatNumber(recency.last7Days)}</strong><span>New in 7 days</span></div>
            </div>
            <div className="admin-period-filter" role="group" aria-label="Account creation period">
                {periods.map(period => (
                    <button aria-pressed={period.key === selectedPeriod} className={period.key === selectedPeriod ? "is-active" : ""} key={period.key} onClick={() => setSelectedPeriod(period.key)} type="button">{period.label}</button>
                ))}
            </div>
            <div className="admin-account-chart" aria-label="Firebase Auth account creation by month" style={{ gridTemplateColumns: `repeat(${months.length}, minmax(36px, 1fr))` }}>
                {months.map(month => (
                    <div className="admin-account-chart__month" key={month.key} title={`${month.count} accounts created in ${month.key}`}>
                        <span>{formatNumber(month.count)}</span><div><i style={{ height: `${Math.max(month.count ? 12 : 3, (month.count / max) * 110)}px` }} /></div><small>{month.label}</small>
                    </div>
                ))}
            </div>
            <h3 className="admin-subheading">Latest users</h3>
            <div className="admin-user-list">
                {recentAccounts.map(user => (
                    <article className="admin-user-row" key={user.uid}>
                        <span className="admin-avatar">{user.photoURL ? <img src={user.photoURL} alt="" /> : (user.displayName || user.email || "?").slice(0, 1).toUpperCase()}</span>
                        <span><strong>{user.displayName || user.email || user.uid}</strong><small>{user.email || user.uid} · joined {formatDate(user.creationTime)}</small></span>
                        <time title={exactDate(user.lastSignInTime)}>Last sign-in {formatDate(user.lastSignInTime)}</time>
                    </article>
                ))}
            </div>
        </section>
    )
}

const DataHealth = ({ health }) => {
    const total = Math.max(1, health.totalTracks)
    const rows = [
        { label: "Indoor", value: health.indoorTracks, tone: "blue" },
        { label: "Outdoor", value: health.outdoorTracks, tone: "orange" },
        { label: "Missing country", value: health.missingCountry, tone: "red" },
    ]
    return (
        <section className="admin-panel admin-health">
            <SectionHeader eyebrow="Coverage" title="Track data health" meta={`${formatNumber(health.totalTracks)} records`} />
            <div className="admin-health__rows">
                {rows.map(row => (
                    <div className="admin-health__row" key={row.label}>
                        <div><span>{row.label}</span><strong>{formatNumber(row.value)}</strong></div>
                        <div className="admin-health__bar"><i className={`admin-health__bar--${row.tone}`} style={{ width: `${Math.min(100, (row.value / total) * 100)}%` }} /></div>
                    </div>
                ))}
            </div>
            <p className="admin-health__note"><Icon name={health.missingCountry ? "warning" : "check"} size={16} /> {health.missingCountry ? `${health.missingCountry} track records need a country.` : "All tracks have country data."}</p>
        </section>
    )
}

const RecentTracks = ({ tracks }) => (
    <section className="admin-panel">
        <SectionHeader eyebrow="Directory" title="Recently added tracks" meta="Published" />
        <div className="admin-track-grid">
            {tracks.map(track => (
                <a href={track.slug ? `/go-karting/${encodeURIComponent(track.slug)}` : "#"} className="admin-track-tile" key={track.id} target="_blank" rel="noreferrer">
                    <span className="admin-track-tile__index">{String(track.id).padStart(2, "0").slice(-2)}</span>
                    <span><strong>{track.name || track.id}</strong><small>{[track.country, track.subtype].filter(Boolean).join(" · ") || "Track record"}</small></span>
                    <Icon name="arrow" size={16} />
                </a>
            ))}
            {tracks.length === 0 && <EmptyState>No timestamped published tracks found.</EmptyState>}
        </div>
    </section>
)

const Dashboard = () => {
    const auth = useContext(FirebaseContext)
    const [access, setAccess] = useState("checking")
    const [verifiedUid, setVerifiedUid] = useState(null)
    const [dashboard, setDashboard] = useState(null)
    const [error, setError] = useState(null)
    const [actionError, setActionError] = useState(null)
    const [approvingTrackId, setApprovingTrackId] = useState(null)
    const [reviewingClaimId, setReviewingClaimId] = useState(null)
    const [reviewingSponsorshipId, setReviewingSponsorshipId] = useState(null)
    const [lastUpdated, setLastUpdated] = useState(null)
    const [refreshing, setRefreshing] = useState(false)
    const [reloadVersion, setReloadVersion] = useState(0)
    const [sections, setSections] = useState({})
    const sectionLoads = useRef(new Set())
    const authGeneration = useRef(0)
    const dataGeneration = useRef(0)
    const actionInProgress = useRef(false)
    const pendingLoad = useRef(false)
    const mounted = useRef(true)
    const projectId = process.env.GATSBY_FB_PROJ_ID || "open-racer-kart-tracks"

    useEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
            authGeneration.current += 1
            dataGeneration.current += 1
        }
    }, [])

    useEffect(() => {
        let active = true
        const generation = ++authGeneration.current
        setAccess("checking")
        setVerifiedUid(null)
        setDashboard(null)
        setSections({})
        sectionLoads.current.clear()
        setLastUpdated(null)
        setRefreshing(false)
        setActionError(null)
        setApprovingTrackId(null)
        setReviewingClaimId(null)
        setReviewingSponsorshipId(null)
        dataGeneration.current += 1

        const checkAccess = async () => {
            if (auth.user === null) return
            if (!auth.user) {
                if (active && generation === authGeneration.current) setAccess("signed-out")
                return
            }
            try {
                const token = await auth.user.getIdTokenResult(true)
                if (active && generation === authGeneration.current) {
                    setVerifiedUid(auth.user.uid)
                    setAccess(token.claims && (token.claims.isAdmin === true || token.claims.isAdmin === "true") ? "allowed" : "denied")
                }
            } catch (accessError) {
                if (active && generation === authGeneration.current) {
                    setVerifiedUid(auth.user.uid)
                    setError(accessError)
                    setAccess("denied")
                }
            }
        }
        checkAccess()
        return () => { active = false }
    }, [auth.user])

    const releaseAction = useCallback(() => {
        actionInProgress.current = false
        if (pendingLoad.current && mounted.current) {
            pendingLoad.current = false
            setReloadVersion(current => current + 1)
        }
    }, [])

    const load = useCallback(async () => {
        if (actionInProgress.current) {
            pendingLoad.current = true
            return
        }
        pendingLoad.current = false
        const generation = authGeneration.current
        const dataVersion = ++dataGeneration.current
        setRefreshing(true)
        setError(null)
        try {
            const firestore = auth.app.firestore()
            const [tracksSnapshot, claimsSnapshot, sponsorshipsSnapshot] = await Promise.all([
                firestore.collection("tracks")
                    .where("status", "==", "pending")
                    .orderBy("createdAt", "desc")
                    .limit(50)
                    .get(),
                firestore.collection("businessClaims")
                    .where("status", "==", "pending")
                    .orderBy("createdAt", "desc")
                    .limit(50)
                    .get(),
                firestore.collection("sponsorshipRequests")
                    .where("status", "==", "pending")
                    .orderBy("createdAt", "desc")
                    .limit(50)
                    .get(),
            ])
            if (!mounted.current || generation !== authGeneration.current || dataVersion !== dataGeneration.current) return
            const documents = snapshot => snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))
            setDashboard(buildAdminDashboard({
                tracks: documents(tracksSnapshot),
                claims: documents(claimsSnapshot),
                sponsorships: documents(sponsorshipsSnapshot),
            }))
            setSections({})
            setLastUpdated(new Date())
        } catch (loadError) {
            if (!mounted.current || generation !== authGeneration.current || dataVersion !== dataGeneration.current) return
            console.error("Failed to load admin dashboard", loadError)
            setError(loadError)
        } finally {
            if (mounted.current && generation === authGeneration.current && dataVersion === dataGeneration.current) setRefreshing(false)
        }
    }, [auth.app, auth.user, projectId])

    const loadRecentLines = useCallback(async () => {
        const firestore = auth.app.firestore()
        const snapshot = await firestore.collection("lines").orderBy("created", "desc").limit(40).get()
        const lines = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))
        const visibleLines = lines.filter(line => lineSource(line).key !== "manual" || line.isCloseDetected === true)
        const users = await loadLineAuthors(firestore, visibleLines)
        return buildAdminDashboard({ lines: visibleLines, users })
    }, [auth.app])

    const loadTrackOverview = useCallback(async () => {
        const snapshot = await auth.app.firestore().collection("tracks").get()
        return buildAdminDashboard({ tracks: snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })) })
    }, [auth.app])

    const loadAccountStats = useCallback(async () => {
        const stats = await loadAdminStats(auth.user, { scope: "accounts" })
        return buildAdminDashboard({ stats })
    }, [auth.user])

    const loadLineStats = useCallback(async () => {
        const stats = await loadAdminStats(auth.user, { linePeriod: "30d", scope: "lines" })
        return buildAdminDashboard({ stats })
    }, [auth.user])

    const toggleSection = useCallback(async (key, loader) => {
        const current = sections[key]
        if (current && current.open) {
            setSections(value => ({ ...value, [key]: { ...value[key], open: false } }))
            return
        }
        if (current && current.data) {
            setSections(value => ({ ...value, [key]: { ...value[key], open: true } }))
            return
        }
        if (sectionLoads.current.has(key)) return
        const generation = authGeneration.current
        const dataVersion = dataGeneration.current
        sectionLoads.current.add(key)
        setSections(value => ({ ...value, [key]: { open: true, loading: true, data: null, error: null } }))
        try {
            const data = await loader()
            if (!mounted.current || generation !== authGeneration.current || dataVersion !== dataGeneration.current) return
            setSections(value => ({ ...value, [key]: { ...value[key], loading: false, data, error: null } }))
        } catch (loadError) {
            if (!mounted.current || generation !== authGeneration.current || dataVersion !== dataGeneration.current) return
            setSections(value => ({ ...value, [key]: { ...value[key], loading: false, data: null, error: loadError } }))
        } finally {
            sectionLoads.current.delete(key)
        }
    }, [sections])

    const loadSourcePeriod = useCallback(async periodKey => {
        const generation = authGeneration.current
        const dataVersion = dataGeneration.current
        try {
            const stats = await loadAdminStats(auth.user, { linePeriod: periodKey, scope: "lines" })
            if (!mounted.current || generation !== authGeneration.current || dataVersion !== dataGeneration.current) return false
            const period = stats.lineSourcePeriods && stats.lineSourcePeriods[0]
            if (!period) throw new Error("The source-period response was empty")
            setSections(current => {
                const section = current.lineStats
                if (!section || !section.data || section.data.lineSourcePeriods.some(item => item.key === period.key)) return current
                return {
                    ...current,
                    lineStats: {
                        ...section,
                        data: { ...section.data, lineSourcePeriods: [...section.data.lineSourcePeriods, period] },
                    },
                }
            })
            return true
        } catch (loadError) {
            if (mounted.current && generation === authGeneration.current && dataVersion === dataGeneration.current) setError(loadError)
            throw loadError
        }
    }, [auth.user])

    const approveTrack = useCallback(async track => {
        if (refreshing || actionInProgress.current) return
        actionInProgress.current = true
        const generation = authGeneration.current
        const dataVersion = ++dataGeneration.current
        setActionError(null)
        setApprovingTrackId(track.id)
        setRefreshing(false)
        try {
            const firestore = auth.app.firestore()
            const reference = firestore.collection("tracks").doc(track.id)
            const timestamp = firebase.firestore.FieldValue.serverTimestamp()
            await firestore.runTransaction(async transaction => {
                const snapshot = await transaction.get(reference)
                if (!snapshot.exists || snapshot.data().status !== "pending") {
                    throw new Error("This track is no longer pending. Refresh the dashboard before trying again.")
                }
                transaction.update(reference, approveTrackUpdate(timestamp))
            })
            if (!mounted.current || generation !== authGeneration.current || dataVersion !== dataGeneration.current) return
            const approvedTrack = { ...track, status: "published", publishedAt: new Date(), updatedAt: new Date() }
            setDashboard(current => current && ({
                ...current,
                metrics: {
                    ...current.metrics,
                    publishedTracks: current.metrics.publishedTracks + 1,
                    pendingSuggestions: Math.max(0, current.metrics.pendingSuggestions - 1),
                },
                pendingTracks: current.pendingTracks.filter(item => item.id !== track.id),
                recentTracks: [approvedTrack, ...current.recentTracks.filter(item => item.id !== track.id)].slice(0, 8),
            }))
            setLastUpdated(new Date())
        } catch (approvalError) {
            if (!mounted.current || generation !== authGeneration.current || dataVersion !== dataGeneration.current) return
            console.error("Failed to approve track", approvalError)
            setActionError(approvalError)
        } finally {
            releaseAction()
            if (mounted.current && generation === authGeneration.current && dataVersion === dataGeneration.current) setApprovingTrackId(null)
        }
    }, [auth.app, refreshing, releaseAction])

    const reviewClaim = useCallback(async (claim, status) => {
        if (!["approved", "rejected"].includes(status)) return
        if (refreshing || actionInProgress.current) return
        actionInProgress.current = true
        const generation = authGeneration.current
        const dataVersion = ++dataGeneration.current
        setActionError(null)
        setReviewingClaimId(claim.id)
        setRefreshing(false)
        try {
            const firestore = auth.app.firestore()
            const reference = firestore.collection("businessClaims").doc(claim.id)
            const timestamp = firebase.firestore.FieldValue.serverTimestamp()
            await firestore.runTransaction(async transaction => {
                const snapshot = await transaction.get(reference)
                if (!snapshot.exists || snapshot.data().status !== "pending") {
                    throw new Error("This claim is no longer pending. Refresh the dashboard before trying again.")
                }
                transaction.update(reference, {
                    status,
                    updatedAt: timestamp,
                    reviewedAt: timestamp,
                    reviewedBy: auth.user.uid,
                })
            })
            if (!mounted.current || generation !== authGeneration.current || dataVersion !== dataGeneration.current) return
            setDashboard(current => current && ({
                ...current,
                metrics: {
                    ...current.metrics,
                    pendingClaims: Math.max(0, current.metrics.pendingClaims - 1),
                },
                pendingClaims: current.pendingClaims.filter(item => item.id !== claim.id),
            }))
            setLastUpdated(new Date())
            if (typeof window !== "undefined" && window.gtag) {
                window.gtag("event", `business_claim_${status}`, { track_id: claim.trackId })
            }
        } catch (reviewError) {
            if (!mounted.current || generation !== authGeneration.current || dataVersion !== dataGeneration.current) return
            console.error("Failed to review business claim", reviewError)
            setActionError(reviewError)
        } finally {
            releaseAction()
            if (mounted.current && generation === authGeneration.current && dataVersion === dataGeneration.current) setReviewingClaimId(null)
        }
    }, [auth.app, auth.user, refreshing, releaseAction])

    const reviewSponsorship = useCallback(async (request, status) => {
        if (!["approved", "rejected"].includes(status)) return
        if (refreshing || actionInProgress.current) return
        actionInProgress.current = true
        const generation = authGeneration.current
        const dataVersion = ++dataGeneration.current
        setActionError(null)
        setReviewingSponsorshipId(request.id)
        setRefreshing(false)
        try {
            const firestore = auth.app.firestore()
            const reference = firestore.collection("sponsorshipRequests").doc(request.id)
            const timestamp = firebase.firestore.FieldValue.serverTimestamp()
            await firestore.runTransaction(async transaction => {
                const snapshot = await transaction.get(reference)
                if (!snapshot.exists || snapshot.data().status !== "pending") {
                    throw new Error("This sponsorship request is no longer pending. Refresh the dashboard before trying again.")
                }
                transaction.update(reference, {
                    status,
                    updatedAt: timestamp,
                    reviewedAt: timestamp,
                    reviewedBy: auth.user.uid,
                })
            })
            if (!mounted.current || generation !== authGeneration.current || dataVersion !== dataGeneration.current) return
            setDashboard(current => current && ({
                ...current,
                metrics: {
                    ...current.metrics,
                    pendingSponsorships: Math.max(0, current.metrics.pendingSponsorships - 1),
                },
                pendingSponsorships: current.pendingSponsorships.filter(item => item.id !== request.id),
            }))
            setLastUpdated(new Date())
            if (typeof window !== "undefined" && window.gtag) {
                window.gtag("event", `sponsorship_request_${status}`, {
                    package_key: request.packageKey,
                    target_key: request.targetKey,
                })
            }
        } catch (reviewError) {
            if (!mounted.current || generation !== authGeneration.current || dataVersion !== dataGeneration.current) return
            console.error("Failed to review sponsorship request", reviewError)
            setActionError(reviewError)
        } finally {
            releaseAction()
            if (mounted.current && generation === authGeneration.current && dataVersion === dataGeneration.current) setReviewingSponsorshipId(null)
        }
    }, [auth.app, auth.user, refreshing, releaseAction])

    const effectiveAccess = auth.user === null
        ? "checking"
        : !auth.user
            ? "signed-out"
            : verifiedUid === auth.user.uid
                ? access
                : "checking"

    useEffect(() => {
        if (effectiveAccess === "allowed") load()
    }, [effectiveAccess, load, reloadVersion])

    const adminActionInProgress = Boolean(approvingTrackId || reviewingClaimId || reviewingSponsorshipId)

    if (effectiveAccess === "checking") return <LoadingState />
    if (effectiveAccess !== "allowed") return <AccessState type={effectiveAccess} />

    return (
        <div className="admin-dashboard">
            <header className="admin-hero">
                <div>
                    <div className="admin-live"><i /> Live Firestore data</div>
                    <p className="admin-eyebrow">Open Racer operations</p>
                    <h1>Race control</h1>
                    <p className="admin-hero__intro">The useful view of what is being added, created and maintained across Open Racer.</p>
                </div>
                <div className="admin-hero__actions">
                    <span>{lastUpdated ? `Updated ${moment(lastUpdated).fromNow()}` : "Waiting for data"}</span>
                    <button onClick={load} disabled={refreshing || Boolean(approvingTrackId) || Boolean(reviewingClaimId) || Boolean(reviewingSponsorshipId)} type="button"><Icon name="refresh" size={17} /> {refreshing ? "Refreshing" : "Refresh"}</button>
                </div>
            </header>

            {error && <div className="admin-error" role="alert"><Icon name="warning" /><span><strong>Dashboard could not load all data.</strong>{error.message}</span><button disabled={refreshing || adminActionInProgress} type="button" onClick={load}>Retry</button></div>}
            {actionError && <div className="admin-error" role="alert"><Icon name="warning" /><span><strong>Admin action failed.</strong>{actionError.message}</span></div>}

            {!dashboard && !error ? <LoadingState /> : dashboard ? (
                <>
                    <section className="admin-metrics admin-metrics--initial" aria-label="Immediate review metrics">
                        <MetricCard label="Pending suggestions" value={dashboard.metrics.pendingSuggestions} note="Loaded first" icon="inbox" accent="orange" />
                        <MetricCard label="Pending claims" value={dashboard.metrics.pendingClaims} note="Loaded first" icon="member" accent="orange" />
                        <MetricCard label="Sponsor interest" value={dashboard.metrics.pendingSponsorships} note="Loaded first" icon="inbox" accent="green" />
                    </section>

                    <PendingSuggestions
                        tracks={dashboard.pendingTracks}
                        projectId={projectId}
                        onApprove={approveTrack}
                        approvingTrackId={approvingTrackId}
                        actionsDisabled={refreshing || adminActionInProgress}
                    />
                    <div className="admin-review-grid">
                        <PendingClaims claims={dashboard.pendingClaims} onReview={reviewClaim} reviewingClaimId={reviewingClaimId} actionsDisabled={refreshing || adminActionInProgress} />
                        <PendingSponsorships requests={dashboard.pendingSponsorships} onReview={reviewSponsorship} reviewingRequestId={reviewingSponsorshipId} actionsDisabled={refreshing || adminActionInProgress} />
                    </div>

                    <div className="admin-lazy-stack">
                        <LazyPanel eyebrow="Community output" title="Latest racing lines" summary="Loads the latest 40 lines and their author profiles" {...sections.lines} onToggle={() => toggleSection("lines", loadRecentLines)}>
                            {sections.lines && sections.lines.data && <RecentLines lines={sections.lines.data.recentLines} usersByUid={new Map(sections.lines.data.users.map(user => [user.uid, user]))} />}
                        </LazyPanel>

                        <LazyPanel eyebrow="Directory" title="Tracks and recent activity" summary="Loads the full track overview only when needed" {...sections.tracks} onToggle={() => toggleSection("tracks", loadTrackOverview)}>
                            {sections.tracks && sections.tracks.data && <>
                                <section className="admin-metrics admin-metrics--inside">
                                    <MetricCard label="Published tracks" value={sections.tracks.data.metrics.publishedTracks} note="Live in directory" icon="track" accent="blue" />
                                </section>
                                <div className="admin-layout admin-layout--equal">
                                    <RecentActivity activity={sections.tracks.data.activity} days={sections.tracks.data.activityByDay} />
                                    <DataHealth health={sections.tracks.data.dataHealth} />
                                </div>
                                <RecentTracks tracks={sections.tracks.data.recentTracks} />
                            </>}
                        </LazyPanel>

                        <LazyPanel eyebrow="Creation methods" title="Racing-line statistics" summary="Starts with a bounded 30-day query; broader periods load on demand" {...sections.lineStats} onToggle={() => toggleSection("lineStats", loadLineStats)}>
                            {sections.lineStats && sections.lineStats.data && <SourceBreakdown periods={sections.lineStats.data.lineSourcePeriods} onLoadPeriod={loadSourcePeriod} />}
                        </LazyPanel>

                        <LazyPanel eyebrow="Community growth" title="Users and account growth" summary="Loads Firebase Auth account history only when expanded" {...sections.accounts} onToggle={() => toggleSection("accounts", loadAccountStats)}>
                            {sections.accounts && sections.accounts.data && <AccountCreationChart periods={sections.accounts.data.accountCreationPeriods} recency={sections.accounts.data.accountRecency} recentAccounts={sections.accounts.data.recentAccounts} />}
                        </LazyPanel>
                    </div>
                </>
            ) : null}
        </div>
    )
}

const Admin = () => (
    <>
        <Helmet>
            <title>Race Control | Open Racer</title>
            <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Layout>
            <ClientOnly>
                <FirebaseProvider>
                    <Dashboard />
                </FirebaseProvider>
            </ClientOnly>
        </Layout>
    </>
)

export default Admin
